import React, { useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { User, UserPlus, FileText, CreditCard, Receipt, Settings, CalendarDays, Dumbbell } from "lucide-react";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const TAP_MOVE_TOLERANCE = 10;

interface ProfileTabsProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  isAdmin: boolean;
  isCoach: boolean;
}

export const ProfileTabs: React.FC<ProfileTabsProps> = ({
  activeTab,
  onTabChange,
  isAdmin,
  isCoach
}) => {
  const { t } = useTranslation("profile");
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { athleteTheme } = useThemeCustomization();
  const isVb20Athlete = athleteTheme.variant === "dark-premium" && !isAdmin && !isCoach;
  const isVb20Coach = athleteTheme.variant === "dark-premium" && isCoach;

  // Touch gesture tracking for tabs
  const startXY = useRef<{ x: number; y: number } | null>(null);
  const startValueRef = useRef<string | null>(null);
  const startTargetRef = useRef<EventTarget | null>(null);
  const movedRef = useRef<boolean>(false);
  const pointerIdRef = useRef<number | null>(null);

  const resetRefs = useCallback(() => {
    startXY.current = null;
    startValueRef.current = null;
    startTargetRef.current = null;
    movedRef.current = false;
    pointerIdRef.current = null;
  }, []);

  const handleTabPointerDown = useCallback((value: string) => (e: React.PointerEvent) => {
    if (e.pointerType === "touch" || e.pointerType === "pen") {
      startXY.current = { x: e.clientX, y: e.clientY };
      startValueRef.current = value;
      startTargetRef.current = e.currentTarget;
      pointerIdRef.current = e.pointerId;
      movedRef.current = false;

      if (typeof (e.currentTarget as any).setPointerCapture === "function") {
        try {
          (e.currentTarget as any).setPointerCapture(e.pointerId);
        } catch (err) {
          console.warn("Failed to capture pointer:", err);
        }
      }
    } else {
      resetRefs();
    }
  }, [resetRefs]);

  const handleTabPointerMove = useCallback((e: React.PointerEvent) => {
    if (!startXY.current) return;

    const dx = Math.abs(e.clientX - startXY.current.x);
    const dy = Math.abs(e.clientY - startXY.current.y);

    if (dx > TAP_MOVE_TOLERANCE || dy > TAP_MOVE_TOLERANCE) {
      movedRef.current = true;
    }
  }, []);

  const handleTabPointerUp = useCallback((value: string) => (e: React.PointerEvent) => {
    if (!e.isPrimary) {
      resetRefs();
      return;
    }

    if (pointerIdRef.current != null && typeof (e.currentTarget as any).releasePointerCapture === "function") {
      try {
        if ((e.currentTarget as any).hasPointerCapture?.(pointerIdRef.current)) {
          (e.currentTarget as any).releasePointerCapture(pointerIdRef.current);
        }
      } catch (err) {
        console.warn("Failed to release pointer:", err);
      }
    }

    const sameValue = startValueRef.current === value;
    const sameTarget = startTargetRef.current === e.currentTarget;
    const didNotMove = !movedRef.current;

    if (!sameValue || !sameTarget || !didNotMove) {
      resetRefs();
      return;
    }

    onTabChange(value);
    resetRefs();
  }, [onTabChange, resetRefs]);

  const handleTabPointerCancel = useCallback(() => {
    resetRefs();
  }, [resetRefs]);

  const gridCols = 'grid-cols-4 md:grid-cols-5';

  if (isVb20Athlete) {
    const athleteTabs = [
      { value: "personal", label: t("athleteNav.personal"), icon: User, onSelect: () => onTabChange("personal") },
      { value: "subscriptions", label: t("athleteNav.subscription"), icon: Dumbbell, onSelect: () => navigate("/athlete/subscriptions") },
      { value: "reservations", label: t("athleteNav.reservations"), icon: CalendarDays, onSelect: () => navigate("/athlete/reservations") },
      { value: "invoices", label: t("athleteNav.payments"), icon: CreditCard, onSelect: () => onTabChange("invoices") },
      { value: "settings", label: t("athleteNav.settings"), icon: Settings, onSelect: () => onTabChange("settings") },
    ];

    return (
      <nav className="athlete-profile-nav grid grid-cols-5 gap-1.5 px-3 py-3" aria-label={t("athleteNav.label")}>
        {athleteTabs.map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.value;
          return (
            <Button
              key={tab.value}
              type="button"
              variant="outline"
              onClick={tab.onSelect}
              className={`flex min-h-[5.5rem] min-w-0 flex-col items-center justify-center gap-2 border px-1 py-2 text-center transition-colors ${selected ? "border-primary bg-primary/10 text-primary" : "border-border bg-card/75 text-foreground hover:border-primary/50"}`}
              aria-current={selected ? "page" : undefined}
            >
              <Icon className="h-6 w-6" strokeWidth={1.7} />
              <span className="text-[10px] font-medium leading-tight md:text-xs">{tab.label}</span>
            </Button>
          );
        })}
      </nav>
    );
  }

  if (isVb20Coach) {
    const coachTabs = [
      { value: "personal", label: t("athleteNav.personal"), icon: User },
      { value: "emergency", label: t("tabs.emergency"), icon: UserPlus },
      { value: "medical", label: t("tabs.medical"), icon: FileText },
      { value: "invoices", label: t("tabs.invoices"), icon: Receipt },
      { value: "settings", label: t("athleteNav.settings"), icon: Settings },
    ];

    return (
      <nav className="coach-profile-nav grid grid-cols-5 gap-1.5 px-3 py-3" aria-label={t("athleteNav.label")}>
        {coachTabs.map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.value;
          return (
            <Button
              key={tab.value}
              type="button"
              variant="outline"
              onClick={() => onTabChange(tab.value)}
              className={`flex min-h-[5.5rem] min-w-0 flex-col items-center justify-center gap-2 border px-1 py-2 text-center transition-colors ${selected ? "border-primary bg-primary/10 text-primary" : "border-border bg-card/75 text-foreground hover:border-primary/50"}`}
              aria-current={selected ? "page" : undefined}
            >
              <Icon className="h-6 w-6" strokeWidth={1.7} />
              <span className="text-[9px] font-medium leading-tight sm:text-xs">{tab.label}</span>
            </Button>
          );
        })}
      </nav>
    );
  }

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="w-full mt-6">
      <TabsList className={`w-full grid ${gridCols} h-auto`}>
        <TabsTrigger 
          value="personal" 
          className="flex items-center gap-2 py-3 touch-manipulation select-none"
          onPointerDown={handleTabPointerDown("personal")}
          onPointerMove={handleTabPointerMove}
          onPointerUp={handleTabPointerUp("personal")}
          onPointerCancel={handleTabPointerCancel}
        >
          <User className="h-4 w-4" />
          {!isMobile && <span>{t("tabs.personal")}</span>}
        </TabsTrigger>
        
        <TabsTrigger 
          value="emergency" 
          className="flex items-center gap-2 py-3 touch-manipulation select-none"
          onPointerDown={handleTabPointerDown("emergency")}
          onPointerMove={handleTabPointerMove}
          onPointerUp={handleTabPointerUp("emergency")}
          onPointerCancel={handleTabPointerCancel}
        >
          <UserPlus className="h-4 w-4" />
          {!isMobile && <span>{t("tabs.emergency")}</span>}
        </TabsTrigger>
        
        <TabsTrigger 
          value="medical" 
          className="flex items-center gap-2 py-3 touch-manipulation select-none"
          onPointerDown={handleTabPointerDown("medical")}
          onPointerMove={handleTabPointerMove}
          onPointerUp={handleTabPointerUp("medical")}
          onPointerCancel={handleTabPointerCancel}
        >
          <FileText className="h-4 w-4" />
          {!isMobile && <span>{t("tabs.medical")}</span>}
        </TabsTrigger>
        
        {/* <TabsTrigger value="payment" className="flex items-center gap-2 py-3 touch-manipulation select-none">
          <CreditCard className="h-4 w-4" />
          {!isMobile && <span>{t("tabs.payment")}</span>}
        </TabsTrigger> */}
        
        <TabsTrigger 
          value="invoices" 
          className="flex items-center gap-2 py-3 touch-manipulation select-none"
          onPointerDown={handleTabPointerDown("invoices")}
          onPointerMove={handleTabPointerMove}
          onPointerUp={handleTabPointerUp("invoices")}
          onPointerCancel={handleTabPointerCancel}
        >
          <Receipt className="h-4 w-4" />
          {!isMobile && <span>{t("tabs.invoices")}</span>}
        </TabsTrigger>

        <TabsTrigger 
          value="settings" 
          className="flex items-center gap-2 py-3 touch-manipulation select-none"
          onPointerDown={handleTabPointerDown("settings")}
          onPointerMove={handleTabPointerMove}
          onPointerUp={handleTabPointerUp("settings")}
          onPointerCancel={handleTabPointerCancel}
        >
          <Settings className="h-4 w-4" />
          {!isMobile && <span>{t("tabs.settings")}</span>}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
};
